import { gql, useMutation, useQuery, useApolloClient } from "@apollo/client";
import React, { useState, useContext, useEffect } from "react";
import {
  LoadingView,
  InvalidReasons,
  NotificationContext,
  ToggleSwitch,
} from "@solsys/solsys-reactjs";
import SaveButton from "../elements/SaveButton";
import { useIdempotentCreate, fingerprintProperties, mutationInput } from "../../utils/idempotency";
import CraneDropDown from "../../dropdowns/CraneDropDown";

const entityQuery = gql`
  query customerList(
    $customer_list_request: customer_list_request
    $crane_list_request: crane_list_request
  ) {
    customer(customer_list_request: $customer_list_request) {
      edges {
        id
        name
        status
        sequence
      }
      totalCount
    }
    crane(crane_list_request: $crane_list_request) {
      edges {
        id
        customer_id
      }
      totalCount
    }
  }
`;

const entityMutation = gql`
  mutation customerMutate($customer_mutation_request: MutationInput) {
    customer(customer_mutation_request: $customer_mutation_request) {
      id
      isNew
    }
  }
`;

const craneMutation = gql`
  mutation craneMutate($crane_mutation_request: MutationInput) {
    crane(crane_mutation_request: $crane_mutation_request) {
      id
      isNew
    }
  }
`;

export default function CustomerEdit({ id: entityId, action, navigate }) {
  const client = useApolloClient();
  const isEdit = action === "edit";
  const { addNotification } = useContext(NotificationContext);

  let variables = {
    customer_list_request: {
      filters: [
        {
          field: "id",
          operator: "=",
          value: String(entityId ?? 0),
        },
      ],
    },
    crane_list_request: {
      pagination: { limit: 10000, offset: 0 },
      filters: [
        {
          field: "customer_id",
          operator: "=",
          value: String(entityId ?? 0),
        },
      ],
    },
  };

  const [name, setName] = useState("");
  const [sequence, setSequence] = useState();
  const [status, setStatus] = useState(isEdit ? 0 : 1);
  const [craneIds, setCraneIds] = useState([]);
  const [craneList, setCraneList] = useState([]);
  const [invalidReasons, setInvalidReasons] = useState([]);
  const createProperties = [
    { key: "name", string: name },
    { key: "sequence", string: sequence },
    { key: "status", number: status ? 1 : 0 },
  ];
  const { idempotencyKey, isSaving, trackSave } = useIdempotentCreate({
    enabled: !isEdit,
    fingerprint: fingerprintProperties(createProperties),
  });

  const {
    loading: loadingEntity,
    refetch: refetchEntity,
  } = useQuery(entityQuery, {
    variables,
    skip: !isEdit,
    fetchPolicy: "network-only",
    onCompleted: (localData) => {
      const customer = localData?.customer?.edges?.[0];
      if (!customer) return;
      const cranes = localData?.crane?.edges ?? [];
      setName(customer.name);
      setSequence(customer.sequence);
      setStatus(customer.status);
      setCraneIds(cranes.map((crane) => crane.id));
      setCraneList(cranes);
    },
    onError: (error) => {
      console.error(error);
    },
  });

  const validateForm = () => {
    const reasons = [];
    if (!name || name.trim() === "") reasons.push("Name is required.");
    setInvalidReasons(reasons);
    return reasons.length === 0;
  };

  const syncCranes = async (customer_id) => {
    const mutateList = [];

    craneList.forEach((crane) => {
      if (!craneIds.some((id) => Number(id) === Number(crane.id))) {
        mutateList.push({
          id: crane.id,
          customer_id: null,
        });
      }
    });

    craneIds.forEach((id) => {
      if (!craneList.some((crane) => Number(crane.id) === Number(id))) {
        mutateList.push({
          id,
          customer_id: Number(customer_id),
        });
      }
    });

    const mutationPromises = mutateList.map((crane) =>
      client.mutate({
        mutation: craneMutation,
        variables: {
          crane_mutation_request: {
            id: crane.id,
            properties: [
              { key: "customer_id", number: crane.customer_id },
            ],
          },
        },
      })
    );

    if (mutationPromises.length === 0) {
      addNotification("good", "Customer saved successfully");
      return;
    }

    try {
      await Promise.all(mutationPromises);
      addNotification("good", "Customer saved successfully");
    } catch (error) {
      console.error("One or more crane assignment mutations failed:", error);
      addNotification("bad", "Customer saved, but updating crane assignments failed");
    }
  };

  const [mutateFunction, mutateInfo] = useMutation(entityMutation, {
    variables: {
      customer_mutation_request: mutationInput({
        id: isEdit ? entityId : undefined,
        properties: createProperties,
        idempotencyKey,
      }),
    },
    onError: (error) => {
      console.error(error);
      addNotification("bad", "Something went wrong");
    },
    onCompleted: async (response) => {
      const id = response?.customer?.id;
      await syncCranes(id);
      navigate({
        action: "edit",
        id,
        push: false,
      });
      refetchEntity();
    },
  });

  useEffect(() => {
    if (isEdit) refetchEntity();
  }, [entityId, isEdit, refetchEntity]);

  const loadingMutation = mutateInfo.loading;

  return loadingEntity && isEdit ? (
    <LoadingView />
  ) : (
    <>
      <div className="page-header">
        <span className="page-header-title">
          {isEdit ? "Edit" : "Add"} Customer
        </span>
      </div>

      <div className="add-edit">
        <div className="edit-section">
          <div className="edit-property">
            <label>Status:</label>
            <ToggleSwitch
              checked={status}
              onChange={() => setStatus(!status)}
              size={{
                width: 60,
                height: 32,
                knob: 24,
                padding: 4,
              }}
            />
          </div>
          <div className="edit-property">
            <label>Name *:</label>
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="edit-property">
            <label>Cranes:</label>
            <CraneDropDown
              selected={craneIds}
              onSelect={setCraneIds}
              searchEnabled
              multiple={true}
            />
          </div>
        </div>
        {invalidReasons.length > 0 && (
          <InvalidReasons reasons={invalidReasons} />
        )}
        <div className="edit-section">
          <SaveButton
            saving={!isEdit && isSaving}
            disabled={isEdit && loadingMutation}
            onClick={() => {
              if (!validateForm()) return;
              if (isEdit) mutateFunction();
              else trackSave(() => mutateFunction());
            }}
          />
        </div>
      </div>
    </>
  );
}
